/**
 * Push Notification Admin Page
 * Quản lý cấu hình push, subscriptions, gửi thông báo và xem thống kê
 */
import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { vi } from 'date-fns/locale'
import clsx from 'clsx'
import {
  HiOutlineBell,
  HiOutlineCog,
  HiOutlineUsers,
  HiOutlinePaperAirplane,
  HiOutlineChartBar,
  HiOutlineRefresh,
  HiOutlineTrash,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineExclamation,
  HiOutlineClipboardCopy,
  HiOutlineSearch,
  HiOutlineFilter,
  HiOutlineDownload,
  HiOutlineDocumentText,
  HiOutlineGlobe,
  HiOutlineDeviceMobile,
  HiOutlineDesktopComputer,
} from 'react-icons/hi'
import pushNotificationService from '../../services/pushNotificationService'

// Tab definitions
const TABS = [
  { id: 'config', label: 'Cấu hình', icon: HiOutlineCog },
  { id: 'subscriptions', label: 'Đăng ký', icon: HiOutlineUsers },
  { id: 'send', label: 'Gửi thông báo', icon: HiOutlinePaperAirplane },
  { id: 'history', label: 'Lịch sử', icon: HiOutlineDocumentText },
  { id: 'stats', label: 'Thống kê', icon: HiOutlineChartBar },
]

// Device type icons
const DEVICE_ICONS = {
  desktop: HiOutlineDesktopComputer,
  mobile: HiOutlineDeviceMobile,
  tablet: HiOutlineDeviceMobile,
}

function PushNotificationAdmin() {
  const [activeTab, setActiveTab] = useState('config')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Config state
  const [config, setConfig] = useState(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState([])
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false)
  const [subscriptionsPagination, setSubscriptionsPagination] = useState({ page: 1, total: 0, pages: 1 })
  const [subscriptionsFilter, setSubscriptionsFilter] = useState({ status: 'all', search: '' })

  // History state
  const [notifications, setNotifications] = useState([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [notificationsPagination, setNotificationsPagination] = useState({ page: 1, total: 0, pages: 1 })

  // Stats state
  const [stats, setStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsPeriod, setStatsPeriod] = useState('7d')

  // Send form state
  const [sendForm, setSendForm] = useState({
    title: '',
    body: '',
    action_url: '',
    icon_url: '',
    target_type: 'all', // all, location, category
    target_locations: [],
    target_categories: [],
  })
  const [sending, setSending] = useState(false)

  // Load config on mount
  useEffect(() => {
    loadConfig()
  }, [])

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      loadSubscriptions()
    } else if (activeTab === 'history') {
      loadNotifications()
    } else if (activeTab === 'stats') {
      loadStats()
    }
  }, [activeTab])

  // ============================================
  // CONFIG TAB
  // ============================================

  const loadConfig = async () => {
    setConfigLoading(true)
    try {
      const response = await pushNotificationService.getConfig()
      if (response.status === 'success') {
        setConfig(response.data)
      }
    } catch (err) {
      console.error('Failed to load config:', err)
    } finally {
      setConfigLoading(false)
    }
  }

  const handleInitConfig = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await pushNotificationService.initConfig()
      if (response.status === 'success') {
        setConfig(response.data)
        setSuccess('Khởi tạo cấu hình thành công!')
      } else {
        setError(response.message)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleEnabled = async () => {
    if (!config) return
    setLoading(true)
    try {
      const response = await pushNotificationService.updateConfig({
        is_enabled: !config.is_enabled,
      })
      if (response.status === 'success') {
        setConfig({ ...config, is_enabled: !config.is_enabled })
        setSuccess(config.is_enabled ? 'Đã tắt Push Notification' : 'Đã bật Push Notification')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerateVapid = async () => {
    if (!confirm('Bạn có chắc muốn tạo lại VAPID keys? Tất cả subscriptions hiện tại sẽ không hoạt động!')) {
      return
    }
    setLoading(true)
    try {
      const response = await pushNotificationService.regenerateVapid()
      if (response.status === 'success') {
        await loadConfig()
        setSuccess('Đã tạo lại VAPID keys!')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerateWebhookSecret = async () => {
    if (!confirm('Bạn có chắc muốn tạo lại Webhook Secret? Bạn sẽ cần cập nhật lại bên BDS News Engine!')) {
      return
    }
    setLoading(true)
    try {
      const response = await pushNotificationService.regenerateWebhookSecret()
      if (response.status === 'success') {
        await loadConfig()
        setSuccess('Đã tạo lại Webhook Secret!')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    setSuccess(`Đã copy ${label}!`)
  }

  // ============================================
  // SUBSCRIPTIONS TAB
  // ============================================

  const loadSubscriptions = async (page = 1) => {
    setSubscriptionsLoading(true)
    try {
      const params = { page, per_page: 20 }
      if (subscriptionsFilter.status !== 'all') {
        params.status = subscriptionsFilter.status
      }
      if (subscriptionsFilter.search) {
        params.search = subscriptionsFilter.search
      }
      const response = await pushNotificationService.getSubscriptions(params)
      if (response.status === 'success') {
        setSubscriptions(response.data.subscriptions || [])
        setSubscriptionsPagination(response.data.pagination || { page: 1, total: 0, pages: 1 })
      }
    } catch (err) {
      console.error('Failed to load subscriptions:', err)
    } finally {
      setSubscriptionsLoading(false)
    }
  }

  const handleDeleteSubscription = async (id) => {
    if (!confirm('Bạn có chắc muốn xóa subscription này?')) return
    try {
      const response = await pushNotificationService.deleteSubscription(id)
      if (response.status === 'success') {
        setSuccess('Đã xóa subscription!')
        loadSubscriptions(subscriptionsPagination.page)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  const handleTestPush = async (subscriptionId) => {
    try {
      const response = await pushNotificationService.testPush(subscriptionId, {
        title: 'Test Push',
        body: 'Đây là tin nhắn test từ Admin',
      })
      if (response.status === 'success') {
        setSuccess('Đã gửi test push!')
      } else {
        setError(response.message)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  const handleCleanupSubscriptions = async () => {
    if (!confirm('Bạn có chắc muốn dọn dẹp các subscriptions không hoạt động?')) return
    setLoading(true)
    try {
      const response = await pushNotificationService.cleanupSubscriptions()
      if (response.status === 'success') {
        setSuccess(`Đã dọn dẹp ${response.data?.cleaned || 0} subscriptions!`)
        loadSubscriptions()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // HISTORY TAB
  // ============================================

  const loadNotifications = async (page = 1) => {
    setNotificationsLoading(true)
    try {
      const response = await pushNotificationService.getNotifications({ page, per_page: 20 })
      if (response.status === 'success') {
        setNotifications(response.data.notifications || [])
        setNotificationsPagination(response.data.pagination || { page: 1, total: 0, pages: 1 })
      }
    } catch (err) {
      console.error('Failed to load notifications:', err)
    } finally {
      setNotificationsLoading(false)
    }
  }

  // ============================================
  // STATS TAB
  // ============================================

  const loadStats = async () => {
    setStatsLoading(true)
    try {
      const response = await pushNotificationService.getStats(statsPeriod)
      if (response.status === 'success') {
        setStats(response.data)
      }
    } catch (err) {
      console.error('Failed to load stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'stats') {
      loadStats()
    }
  }, [statsPeriod])

  // ============================================
  // SEND TAB
  // ============================================

  const handleSendPush = async (e) => {
    e.preventDefault()
    if (!sendForm.title || !sendForm.body) {
      setError('Vui lòng nhập tiêu đề và nội dung')
      return
    }

    setSending(true)
    setError(null)
    try {
      const data = {
        title: sendForm.title,
        body: sendForm.body,
        action_url: sendForm.action_url || undefined,
        icon_url: sendForm.icon_url || undefined,
      }

      if (sendForm.target_type === 'location' && sendForm.target_locations.length > 0) {
        data.target_locations = sendForm.target_locations
      }
      if (sendForm.target_type === 'category' && sendForm.target_categories.length > 0) {
        data.target_categories = sendForm.target_categories
      }

      const response = await pushNotificationService.sendNotification(data)
      if (response.status === 'success') {
        setSuccess(`Gửi thành công! Đã gửi đến ${response.data?.sent || 0} thiết bị`)
        setSendForm({
          title: '',
          body: '',
          action_url: '',
          icon_url: '',
          target_type: 'all',
          target_locations: [],
          target_categories: [],
        })
      } else {
        setError(response.message)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error])

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderConfigTab = () => {
    if (configLoading) {
      return (
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-100 rounded-xl" />
          <div className="h-48 bg-gray-100 rounded-xl" />
        </div>
      )
    }

    if (!config) {
      return (
        <div className="text-center py-12">
          <HiOutlineCog className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Chưa có cấu hình Push Notification
          </h3>
          <p className="text-gray-500 mb-6">
            Khởi tạo cấu hình để bắt đầu sử dụng Push Notification
          </p>
          <button
            onClick={handleInitConfig}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Đang khởi tạo...' : 'Khởi tạo cấu hình'}
          </button>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Status Card */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Trạng thái</h3>
            <button
              onClick={handleToggleEnabled}
              disabled={loading}
              className={clsx(
                'px-4 py-2 rounded-lg font-medium transition-colors',
                config.is_enabled
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {config.is_enabled ? (
                <>
                  <HiOutlineCheck className="w-4 h-4 inline mr-1" />
                  Đang bật
                </>
              ) : (
                <>
                  <HiOutlineX className="w-4 h-4 inline mr-1" />
                  Đang tắt
                </>
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500">
            {config.is_enabled
              ? 'Push Notification đang hoạt động. User có thể đăng ký nhận thông báo.'
              : 'Push Notification đã tắt. User sẽ không thể đăng ký mới.'}
          </p>
        </div>

        {/* VAPID Keys */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">VAPID Keys</h3>
            <button
              onClick={handleRegenerateVapid}
              disabled={loading}
              className="text-sm text-red-600 hover:text-red-700"
            >
              <HiOutlineRefresh className="w-4 h-4 inline mr-1" />
              Tạo lại
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Public Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={config.vapid_public_key || ''}
                  readOnly
                  className="input flex-1 bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(config.vapid_public_key, 'VAPID Public Key')}
                  className="btn btn-secondary"
                >
                  <HiOutlineClipboardCopy className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VAPID Subject
              </label>
              <input
                type="text"
                value={config.vapid_subject || 'mailto:admin@congcumarketing.com'}
                readOnly
                className="input bg-gray-50 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Webhook Config */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Webhook</h3>
            <button
              onClick={handleRegenerateWebhookSecret}
              disabled={loading}
              className="text-sm text-red-600 hover:text-red-700"
            >
              <HiOutlineRefresh className="w-4 h-4 inline mr-1" />
              Tạo lại Secret
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook URL
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/api/push/webhook`}
                  readOnly
                  className="input flex-1 bg-gray-50 text-sm"
                />
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/api/push/webhook`, 'Webhook URL')}
                  className="btn btn-secondary"
                >
                  <HiOutlineClipboardCopy className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook Secret
              </label>
              <div className="flex items-center gap-2">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={config.webhook_secret || ''}
                  readOnly
                  className="input flex-1 bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="btn btn-secondary"
                  title={showWebhookSecret ? 'Ẩn' : 'Hiện'}
                >
                  {showWebhookSecret ? (
                    <HiOutlineEyeOff className="w-5 h-5" />
                  ) : (
                    <HiOutlineEye className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(config.webhook_secret, 'Webhook Secret')}
                  className="btn btn-secondary"
                  title="Copy"
                >
                  <HiOutlineClipboardCopy className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Cung cấp secret này cho BDS News Engine để xác thực webhook
              </p>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-sm text-gray-500">
          <p>Cập nhật lần cuối: {config.updated_at ? format(new Date(config.updated_at), 'dd/MM/yyyy HH:mm', { locale: vi }) : 'N/A'}</p>
        </div>
      </div>
    )
  }

  const renderSubscriptionsTab = () => {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm kiếm theo browser, OS..."
                  value={subscriptionsFilter.search}
                  onChange={(e) => setSubscriptionsFilter({ ...subscriptionsFilter, search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && loadSubscriptions()}
                  className="input pl-10"
                />
              </div>
            </div>
            <select
              value={subscriptionsFilter.status}
              onChange={(e) => {
                setSubscriptionsFilter({ ...subscriptionsFilter, status: e.target.value })
                setTimeout(() => loadSubscriptions(), 0)
              }}
              className="input w-auto"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="active">Đang hoạt động</option>
              <option value="inactive">Không hoạt động</option>
            </select>
            <button
              onClick={() => loadSubscriptions()}
              className="btn btn-secondary"
            >
              <HiOutlineRefresh className="w-5 h-5" />
            </button>
            <button
              onClick={handleCleanupSubscriptions}
              disabled={loading}
              className="btn btn-secondary text-red-600"
            >
              <HiOutlineTrash className="w-5 h-5 mr-1" />
              Dọn dẹp
            </button>
          </div>
        </div>

        {/* List */}
        <div className="card overflow-hidden">
          {subscriptionsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Chưa có subscription nào
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Thiết bị
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Preferences
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Đăng ký
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscriptions.map((sub) => {
                    const DeviceIcon = DEVICE_ICONS[sub.device_type] || HiOutlineGlobe
                    return (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <DeviceIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <div className="font-medium text-gray-900 text-sm">
                                {sub.browser || 'Unknown'} / {sub.os || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-[200px]">
                                {sub.endpoint?.substring(0, 50)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {sub.preferences?.locations?.map((loc) => (
                              <span key={loc} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                {loc}
                              </span>
                            ))}
                            {sub.preferences?.categories?.map((cat) => (
                              <span key={cat} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                {cat}
                              </span>
                            ))}
                            {(!sub.preferences?.locations?.length && !sub.preferences?.categories?.length) && (
                              <span className="text-xs text-gray-400">Tất cả</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={clsx(
                              'px-2 py-1 rounded text-xs font-medium',
                              sub.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            )}
                          >
                            {sub.is_active ? 'Hoạt động' : 'Không hoạt động'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {sub.created_at ? format(new Date(sub.created_at), 'dd/MM/yyyy', { locale: vi }) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleTestPush(sub.id)}
                              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                              title="Gửi test"
                            >
                              <HiOutlinePaperAirplane className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSubscription(sub.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Xóa"
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {subscriptionsPagination.pages > 1 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Trang {subscriptionsPagination.page} / {subscriptionsPagination.pages} ({subscriptionsPagination.total} kết quả)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadSubscriptions(subscriptionsPagination.page - 1)}
                      disabled={subscriptionsPagination.page <= 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Trước
                    </button>
                    <button
                      onClick={() => loadSubscriptions(subscriptionsPagination.page + 1)}
                      disabled={subscriptionsPagination.page >= subscriptionsPagination.pages}
                      className="btn btn-secondary btn-sm"
                    >
                      Sau
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderSendTab = () => {
    const LOCATIONS = ['da-nang', 'quang-nam', 'hcm', 'ha-noi', 'khac']
    const CATEGORIES = ['thi-truong', 'du-an', 'chinh-sach', 'phan-tich', 'khac']

    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Gửi thông báo thủ công
          </h3>

          <form onSubmit={handleSendPush} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tiêu đề <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={sendForm.title}
                onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                placeholder="Tiêu đề thông báo"
                className="input"
                maxLength={100}
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nội dung <span className="text-red-500">*</span>
              </label>
              <textarea
                value={sendForm.body}
                onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                placeholder="Nội dung thông báo"
                className="input"
                rows={3}
                maxLength={200}
              />
            </div>

            {/* Action URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link khi click
              </label>
              <input
                type="url"
                value={sendForm.action_url}
                onChange={(e) => setSendForm({ ...sendForm, action_url: e.target.value })}
                placeholder="https://..."
                className="input"
              />
            </div>

            {/* Target Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gửi đến
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="target_type"
                    value="all"
                    checked={sendForm.target_type === 'all'}
                    onChange={(e) => setSendForm({ ...sendForm, target_type: e.target.value })}
                    className="text-primary-600"
                  />
                  <span className="text-sm">Tất cả</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="target_type"
                    value="location"
                    checked={sendForm.target_type === 'location'}
                    onChange={(e) => setSendForm({ ...sendForm, target_type: e.target.value })}
                    className="text-primary-600"
                  />
                  <span className="text-sm">Theo khu vực</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="target_type"
                    value="category"
                    checked={sendForm.target_type === 'category'}
                    onChange={(e) => setSendForm({ ...sendForm, target_type: e.target.value })}
                    className="text-primary-600"
                  />
                  <span className="text-sm">Theo danh mục</span>
                </label>
              </div>
            </div>

            {/* Location selector */}
            {sendForm.target_type === 'location' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn khu vực
                </label>
                <div className="flex flex-wrap gap-2">
                  {LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        const newLocs = sendForm.target_locations.includes(loc)
                          ? sendForm.target_locations.filter((l) => l !== loc)
                          : [...sendForm.target_locations, loc]
                        setSendForm({ ...sendForm, target_locations: newLocs })
                      }}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        sendForm.target_locations.includes(loc)
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {loc.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Category selector */}
            {sendForm.target_type === 'category' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn danh mục
                </label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        const newCats = sendForm.target_categories.includes(cat)
                          ? sendForm.target_categories.filter((c) => c !== cat)
                          : [...sendForm.target_categories, cat]
                        setSendForm({ ...sendForm, target_categories: newCats })
                      }}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        sendForm.target_categories.includes(cat)
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {cat.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="text-xs text-gray-500 mb-2">Xem trước:</div>
              <div className="bg-white rounded-lg shadow-sm p-3 flex items-start gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiOutlineBell className="w-5 h-5 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">
                    {sendForm.title || 'Tiêu đề thông báo'}
                  </div>
                  <div className="text-gray-500 text-xs mt-0.5 line-clamp-2">
                    {sendForm.body || 'Nội dung thông báo'}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={sending || !sendForm.title || !sendForm.body}
              className="btn btn-primary w-full"
            >
              {sending ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Đang gửi...
                </>
              ) : (
                <>
                  <HiOutlinePaperAirplane className="w-5 h-5 mr-2" />
                  Gửi thông báo
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    )
  }

  const renderHistoryTab = () => {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Lịch sử thông báo</h3>
          <button
            onClick={() => loadNotifications()}
            className="btn btn-secondary btn-sm"
          >
            <HiOutlineRefresh className="w-4 h-4 mr-1" />
            Làm mới
          </button>
        </div>

        {/* List */}
        <div className="card overflow-hidden">
          {notificationsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Chưa có thông báo nào được gửi
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nội dung
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nguồn
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kết quả
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Thời gian
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {notifications.map((notif) => (
                    <tr key={notif.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 text-sm">
                          {notif.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[300px]">
                          {notif.body}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            notif.source_type === 'webhook'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          )}
                        >
                          {notif.source_type === 'webhook' ? 'Webhook' : 'Thủ công'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-green-600">
                            <HiOutlineCheck className="w-4 h-4 inline" />
                            {notif.total_sent || 0}
                          </span>
                          {notif.total_failed > 0 && (
                            <span className="text-sm text-red-600">
                              <HiOutlineX className="w-4 h-4 inline" />
                              {notif.total_failed}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {notif.sent_at
                          ? format(new Date(notif.sent_at), 'dd/MM/yyyy HH:mm', { locale: vi })
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {notificationsPagination.pages > 1 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Trang {notificationsPagination.page} / {notificationsPagination.pages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadNotifications(notificationsPagination.page - 1)}
                      disabled={notificationsPagination.page <= 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Trước
                    </button>
                    <button
                      onClick={() => loadNotifications(notificationsPagination.page + 1)}
                      disabled={notificationsPagination.page >= notificationsPagination.pages}
                      className="btn btn-secondary btn-sm"
                    >
                      Sau
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderStatsTab = () => {
    return (
      <div className="space-y-6">
        {/* Period selector */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Thống kê</h3>
          <select
            value={statsPeriod}
            onChange={(e) => setStatsPeriod(e.target.value)}
            className="input w-auto"
          >
            <option value="24h">24 giờ qua</option>
            <option value="7d">7 ngày qua</option>
            <option value="30d">30 ngày qua</option>
          </select>
        </div>

        {statsLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-xl" />
              ))}
            </div>
          </div>
        ) : stats ? (
          <>
            {/* Summary cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card p-4">
                <div className="text-sm text-gray-500">Tổng subscriptions</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.subscriptions?.total || 0}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {stats.subscriptions?.active || 0} đang hoạt động
                </div>
              </div>

              <div className="card p-4">
                <div className="text-sm text-gray-500">Thông báo đã gửi</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.notifications?.total || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Trong {statsPeriod === '24h' ? '24 giờ' : statsPeriod === '7d' ? '7 ngày' : '30 ngày'}
                </div>
              </div>

              <div className="card p-4">
                <div className="text-sm text-gray-500">Tỷ lệ gửi thành công</div>
                <div className="text-2xl font-bold text-green-600 mt-1">
                  {stats.delivery?.success_rate?.toFixed(1) || 0}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {stats.delivery?.sent || 0} / {stats.delivery?.total || 0}
                </div>
              </div>

              <div className="card p-4">
                <div className="text-sm text-gray-500">Webhook nhận</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.webhooks?.total || 0}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {stats.webhooks?.success || 0} thành công
                </div>
              </div>
            </div>

            {/* Device breakdown */}
            {stats.subscriptions?.by_device && (
              <div className="card p-6">
                <h4 className="font-medium text-gray-900 mb-4">Theo thiết bị</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  {Object.entries(stats.subscriptions.by_device).map(([device, count]) => {
                    const Icon = DEVICE_ICONS[device] || HiOutlineGlobe
                    return (
                      <div key={device} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Icon className="w-8 h-8 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 capitalize">{device}</div>
                          <div className="text-lg font-bold text-gray-900">{count}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Browser breakdown */}
            {stats.subscriptions?.by_browser && (
              <div className="card p-6">
                <h4 className="font-medium text-gray-900 mb-4">Theo trình duyệt</h4>
                <div className="space-y-2">
                  {Object.entries(stats.subscriptions.by_browser).map(([browser, count]) => {
                    const total = stats.subscriptions.total || 1
                    const percent = (count / total) * 100
                    return (
                      <div key={browser} className="flex items-center gap-4">
                        <div className="w-24 text-sm text-gray-600">{browser}</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div
                            className="bg-primary-500 rounded-full h-2"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <div className="w-16 text-right text-sm font-medium text-gray-900">
                          {count}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Không có dữ liệu thống kê
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <HiOutlineBell className="w-6 h-6 mr-2 text-gray-500" />
          Push Notification
        </h1>
        <p className="text-gray-500 mt-1">
          Quản lý thông báo đẩy cho người dùng
        </p>
      </div>

      {/* Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <HiOutlineCheck className="w-5 h-5 text-green-600" />
          <span className="text-green-700">{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <HiOutlineExclamation className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex overflow-x-auto gap-1 p-1 bg-gray-100 rounded-xl">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'config' && renderConfigTab()}
        {activeTab === 'subscriptions' && renderSubscriptionsTab()}
        {activeTab === 'send' && renderSendTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'stats' && renderStatsTab()}
      </div>
    </div>
  )
}

export default PushNotificationAdmin
